import { useState } from "react";
import { AppWindow, ChevronDown, Check, ExternalLink } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "@/store/useSession";
import { getLicensedApps, isSafeAppUrl, type LicensedApp } from "@/api/foxia-apps";
import { getCrmLoginUrl } from "@/lib/urls";

function initials(name: string, email: string): string {
  const source = name.trim() || email.trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length > 1) return `${parts[0][0]}${parts.at(-1)?.[0] ?? ""}`.toUpperCase();
  return source.slice(0, 2).toUpperCase() || "U";
}

export default function FrontendSessionBar({
  compact = false,
  inline = false,
}: {
  compact?: boolean;
  inline?: boolean;
}) {
  const location = useLocation();
  const user = useSession((state) => state.user);
  const [open, setOpen] = useState(false);
  const workspaceId = user?.workspace_id;
  const {
    data: apps = [],
    isLoading: appsLoading,
    isError: appsError,
  } = useQuery<LicensedApp[]>({
    queryKey: ["licensed-apps", workspaceId],
    queryFn: getLicensedApps,
    enabled: Boolean(workspaceId && open),
    staleTime: 5 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  if (!user) return null;

  const displayName =
    (typeof user.name === "string" ? user.name.trim() : "") ||
    (typeof user.email === "string" ? user.email.trim() : "") ||
    "Người dùng Foxia";
  const inlineShortName = displayName.split(/\s+/).at(-1) || displayName;
  const workspaceName = user.workspace_name?.trim() || "Workspace hiện tại";
  const returnTo = `${location.pathname}${location.search}${location.hash}`;
  const switchWorkspaceURL = new URL(getCrmLoginUrl(returnTo));
  switchWorkspaceURL.searchParams.set("select_workspace", "1");

  return (
    <div
      className={
        inline
          ? "relative flex min-w-0 items-center"
          : compact
            ? "fixed right-4 top-3 z-[60] rounded-lg border border-slate-200/80 bg-white/95 shadow-lg backdrop-blur"
            : "fixed inset-x-0 top-0 z-40 border-b border-slate-200/80 bg-white/95 shadow-sm backdrop-blur"
      }
    >
      <div
        className={
          inline
            ? "flex min-w-0 items-center gap-2"
            : compact
            ? "flex h-12 items-center gap-2 px-2"
            : "mx-auto flex h-14 max-w-7xl items-center justify-end gap-3 px-4 sm:px-6"
        }
      >
        <div className="relative">
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
            aria-haspopup="menu"
            aria-label="Mở thông tin tài khoản"
            className="flex min-w-0 items-center gap-2 rounded-md px-1.5 py-1 text-left transition hover:bg-slate-100 active:scale-[0.98]"
          >
            {user.avatar ? (
              <img src={user.avatar} alt={displayName} className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-100 text-xs font-bold text-orange-700">
                {initials(displayName, user.email)}
              </span>
            )}
            <span className={`${inline ? "max-w-16 text-xs sm:max-w-40 sm:text-sm" : `${compact ? "max-w-28" : "max-w-40"} hidden text-sm sm:block`} truncate font-semibold text-slate-800`}>
              <span className="sm:hidden">{inline ? inlineShortName : displayName}</span>
              <span className="hidden sm:inline">{displayName}</span>
            </span>
            <ChevronDown size={15} className="hidden shrink-0 text-slate-400 sm:block" aria-hidden="true" />
          </button>

          {open && (
            <div
              role="menu"
              className="absolute right-0 mt-2 w-[min(23rem,calc(100vw-1.5rem))] overflow-hidden rounded-xl border border-slate-200 bg-slate-50 p-2 shadow-[0_18px_50px_rgba(15,23,42,0.16)]"
            >
              <div className="bg-white px-4 py-4 text-center">
                {user.avatar ? (
                  <img src={user.avatar} alt="" className="mx-auto h-14 w-14 rounded-full object-cover" />
                ) : (
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-orange-100 text-base font-bold text-orange-700">
                    {initials(displayName, user.email)}
                  </span>
                )}
                <p className="mt-2 truncate text-sm font-semibold text-slate-900">{displayName}</p>
                <p className="truncate text-xs text-slate-500">{user.email}</p>
              </div>

              <div className="mt-2 flex items-center gap-3 bg-white px-3 py-3">
                <Check size={17} className="shrink-0 text-orange-600" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-medium text-slate-500">Workspace hiện tại</p>
                  <p className="truncate text-sm font-semibold text-slate-800">{workspaceName}</p>
                </div>
                <a
                  role="menuitem"
                  href={switchWorkspaceURL.toString()}
                  onClick={() => setOpen(false)}
                  className="shrink-0 rounded-md px-2 py-1.5 text-xs font-semibold text-orange-700 transition hover:bg-orange-50"
                >
                  Chuyển
                </a>
              </div>

              <div className="mt-2 bg-white px-3 pb-3 pt-3">
                <div className="mb-2 flex items-center gap-2 px-1">
                  <AppWindow size={16} className="text-slate-500" aria-hidden="true" />
                  <p className="text-sm font-semibold text-slate-800">Ứng dụng của bạn</p>
                </div>
              {appsLoading && <p className="px-2 py-4 text-center text-xs text-slate-500">Đang tải ứng dụng được cấp quyền…</p>}
              {!appsLoading && appsError && <p className="px-2 py-4 text-center text-xs text-slate-500">Chưa tải được danh sách ứng dụng.</p>}
              {!appsLoading && !appsError && apps.length === 0 && <p className="px-2 py-4 text-center text-xs text-slate-500">Workspace này chưa có ứng dụng khả dụng.</p>}
              {!appsLoading && !appsError && apps.length > 0 && <div className="grid grid-cols-3 gap-1">
                {apps.map((licensedApp) => {
                  const app = licensedApp.foxia_app;
                  if (!app || !isSafeAppUrl(app.url)) return null;
                  const label = app.name?.trim() || "Ứng dụng Foxia";
                  return (
                    <a
                      key={licensedApp.id}
                      role="menuitem"
                      href={app.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setOpen(false)}
                      className="flex min-w-0 flex-col items-center gap-1.5 rounded-lg px-1 py-3 text-center transition hover:bg-slate-100 active:scale-[0.98]"
                    >
                      {isSafeAppUrl(app.icon) && <img src={app.icon} alt="" className="h-10 w-10 rounded-lg object-cover" />}
                      {!isSafeAppUrl(app.icon) && <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100 text-xs font-bold text-orange-700">{label.slice(0, 2).toUpperCase()}</span>}
                      <span className="block w-full truncate text-xs font-medium text-slate-700">{label}</span>
                    </a>
                  );
                })}
              </div>}
              </div>

              <a
                role="menuitem"
                href={switchWorkspaceURL.toString()}
                onClick={() => setOpen(false)}
                className="mt-2 flex items-center justify-center gap-2 bg-white px-3 py-2.5 text-xs font-semibold text-slate-600 transition hover:text-orange-700"
              >
                Quản lý tài khoản và workspace
                <ExternalLink size={14} aria-hidden="true" />
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
